import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { useGridEdits } from './gridEdit';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  type ColDef,
  ModuleRegistry,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';
import { signColor, pct, colors, cardGrid, titleRow, gridTitle, gridHint, btnPrimary, agGridBox } from '../styles/fdsTheme';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/**
 * 전략 슬롯 관리 — 계좌(kispfstrat)에 전략을 배정자금·파라미터와 함께 붙인다 (2026-10-24).
 * Quant > 전략관리 화면 아래 · Admin > 계좌·전략 관리 아래. 성과는 포트폴리오 > 전략별 성과(StrategySlotReview).
 *
 * 계좌는 상단 전역 선택(useAuth().selectedPfId), pfId prop 을 주면 그 계좌.
 *   GET/POST/DELETE /api/v1/pfstrat/slots  (PfStratEndpoints)
 *
 * 편집 그리드 표준(fdsapp-ui §14, gridEdit.ts) — 2026-11-03 모달을 없앴다:
 *   [행 추가] → 새 행에서 전략(✎)을 고르고 칸을 채운다 · ✎ 칸 바로 수정 · [변경 저장] 한 번 · 체크 후 [삭제]
 * - 시스템 슬롯(MANUAL·ENS·TA)은 배정자금·메모만 고칠 수 있다
 * - 주문이 있는 슬롯은 지울 수 없다 — '사용' 을 해제한다
 * - fdsintraday 는 주기마다 슬롯을 다시 읽어 재시작 없이 반영된다
 */

interface Slot {
  id: number;
  pfId: number;
  stratId: number;
  code?: string;
  stratName?: string;
  type?: string;
  isSystem: boolean;
  name: string;
  params?: string | null;
  defaultParams?: string | null;
  allocAmt: number;
  enabled: boolean;
  isDefault: boolean;
  startD?: string | null;
  endD?: string | null;
  memo?: string | null;
  nOrder: number;
  // 누적 성과 (perfFrom ~ 오늘, StratPerf) — 주문 체결이 없으면 null
  pnl?: number | null;          // 순손익 = 실현 + 평가 − 수수료 − 세금
  retPct?: number | null;       // 배정자금 대비 %
  realized?: number | null;
  unrealized?: number | null;
  fee?: number | null;          // 추정 수수료 (0.015% 양방향)
  tax?: number | null;          // 추정 거래세 (매도 0.20%, ETF·ETN 면제)
}

interface Strat { id: number; code: string; name: string; type: string; defaultParams: string | null; enabled: boolean; }

/** 슬롯 ↔ 사용자 연결 (fdsuserpf, pfStratId=null 은 계좌 전체) — Dev 만 본다 (2026-10-27) */
interface Grant { id: number; userId: number; email?: string; userName?: string; userRole?: number; pfStratId?: number | null; canOrder: boolean; memo?: string | null; }
interface UserInfo { id: number; email: string; name: string; role: number; }
const roleNm = (r?: number) => r === 15 ? 'API주문' : r === 5 ? 'Viewer' : r === 0 ? 'None' : r === 90 ? 'Dev' : '내부';

/** jsonb 는 문자열·객체 어느 쪽으로도 올 수 있다 */
const paramStr = (v: unknown) => v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v);
const won = (v: number) => Math.round(v).toLocaleString('ko-KR');
const TYPE_BADGE: Record<string, [string, string, string]> = {
  daily: ['일봉', '#dbeafe', '#1e40af'], intraday: ['인트라데이', '#fef3c7', '#92400e'], external: ['외부API', '#ede9fe', '#6d28d9'], system: ['시스템', '#f3f4f6', '#4b5563'],
};

const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', border: `1px solid ${colors.inputBorder}`, borderRadius: '3px', fontSize: '12px' };
const btn = (bg: string): React.CSSProperties => ({ ...btnPrimary, backgroundColor: bg });
const btnSm = (bg: string): React.CSSProperties => ({ padding: '2px 8px', backgroundColor: bg, color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' });

/**
 * pfId 를 주면 전역 선택 대신 그 계좌를 본다 — Admin > 계좌·전략 관리(AccountAdmin)가 계좌를 골라 끼운다 (2026-11-03).
 * 그 화면은 Dev 전용이라 슬롯 수정 권한도 Dev 여부로 판단한다.
 */
const StrategySlotManager: React.FC<{ pfId?: number }> = ({ pfId: pfIdProp }) => {
  const copyMenu = useGridCopyMenu();
  const gridRef = useRef<AgGridReact<Slot>>(null);
  const auth = useAuth();
  const { isDev } = auth;
  const pfId = pfIdProp ?? auth.selectedPfId;
  const canOrder = pfIdProp != null ? isDev : auth.canOrder;   // 슬롯 추가·수정·삭제는 주문 권한이 있어야 한다 (서버도 막는다)
  // 사용자 → 계좌 → 슬롯 → (내부·외부) 사용자. 연결 관리는 Dev 만 (서버 AdminPolicy)
  const [grants, setGrants] = useState<Grant[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [linkSlot, setLinkSlot] = useState<Slot | null>(null);
  const [linkForm, setLinkForm] = useState({ userId: '' as number | '', canOrder: true, memo: '' });
  const fetchGrants = useCallback(async () => {
    if (!isDev || !pfId) { setGrants([]); return; }
    try {
      const res = await getMainAxios().get('/api/v1/auth/pf-grants', { params: { pfId } });
      setGrants(res.data as Grant[]);
    } catch { setGrants([]); }
  }, [isDev, pfId]);
  useEffect(() => { fetchGrants(); }, [fetchGrants]);
  useEffect(() => {
    if (!isDev) return;
    getMainAxios().get('/api/v1/auth/users')
      .then(res => setUsers((res.data as UserInfo[]).filter(u => u.role > 0 && u.role < 90)))
      .catch(() => setUsers([]));
  }, [isDev]);
  const slotGrants = useCallback((slotId: number) => grants.filter(g => g.pfStratId === slotId), [grants]);
  const acctGrants = useMemo(() => grants.filter(g => g.pfStratId == null), [grants]);

  const addLink = async () => {
    if (!linkSlot || !pfId || linkForm.userId === '') { alert('사용자를 선택하세요.'); return; }
    try {
      await getMainAxios().post('/api/v1/auth/pf-grants', {
        userId: linkForm.userId, pfId, pfStratId: linkSlot.id, canOrder: linkForm.canOrder, memo: linkForm.memo.trim() || null,
      });
      setLinkForm({ userId: '', canOrder: true, memo: '' });
      fetchGrants();
    } catch (err: any) { alert(err.response?.data?.error || '연결 실패'); }
  };
  const removeLink = async (g: Grant) => {
    if (!confirm(`${g.userName ?? g.email} 의 슬롯 연결을 끊습니다.`)) return;
    try {
      await getMainAxios().delete(`/api/v1/auth/pf-grants/${g.id}`);
      fetchGrants();
    } catch (err: any) { alert(err.response?.data?.error || '해제 실패'); }
  };
  const [slots, setSlots] = useState<Slot[]>([]);
  const [initAmt, setInitAmt] = useState(0);
  const [perfFrom, setPerfFrom] = useState<string | null>(null);
  const [strats, setStrats] = useState<Strat[]>([]);
  const [showSystem, setShowSystem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchSlots = useCallback(async () => {
    if (!pfId) { setSlots([]); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await getMainAxios().get('/api/v1/pfstrat/slots', { params: { pfId } });
      setSlots(res.data.slots as Slot[]);
      setInitAmt(res.data.initAmt ?? 0);
      setPerfFrom(res.data.perfFrom ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || '슬롯 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [pfId]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);
  useEffect(() => {
    getMainAxios().get('/api/v1/quant/strategies')
      .then(res => setStrats(res.data as Strat[]))
      .catch(() => setStrats([]));
  }, []);

  const shown = useMemo(() => slots.filter(s => showSystem || !s.isSystem), [slots, showSystem]);
  const allocSum = useMemo(() => slots.filter(s => s.enabled && !s.isSystem).reduce((a, s) => a + s.allocAmt, 0), [slots]);
  const userStrats = useMemo(() => strats.filter(s => s.type !== 'system'), [strats]);
  const stratOf = useCallback((id?: number | null) => strats.find(s => s.id === id), [strats]);

  // ── 편집 그리드 ──
  const ed = useGridEdits<Slot>(x => x.id);
  const viewShown = useMemo(() => ed.apply(shown).map(r => {
    if (r.id > 0) return r;
    const st = stratOf(r.stratId);   // 새 행 — 고른 전략으로 표시값을 채운다
    return { ...r, code: st?.code, stratName: st?.name, type: st?.type, defaultParams: st?.defaultParams };
  }), [shown, ed.apply, stratOf]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { ed.reset(); }, [pfId]);
  const canEdit = (f: string) => (p: any) => canOrder && !!p.data
    && (f === 'stratId' ? p.data.id < 0 : (!p.data.isSystem || f === 'allocAmt' || f === 'memo'));

  const addRow = () => {
    if (!pfId) return;
    ed.add(id => ({
      id, pfId, stratId: 0, isSystem: false, name: '', params: null, isDefault: false, nOrder: 0,
      allocAmt: initAmt > allocSum ? Math.round(initAmt - allocSum) : 0, enabled: true,
      startD: new Date().toLocaleDateString('sv-SE'), endD: null, memo: null,
    }));
  };

  const saveAll = async () => {
    if (!pfId) return;
    const rows = [
      ...ed.pending.added,
      ...ed.pending.changed.map(id => ({ ...slots.find(x => x.id === id)!, ...ed.edits[id] }) as Slot).filter(x => x.pfId),
    ];
    const fail: string[] = []; const ok: number[] = [];
    setSaving(true);
    for (const m of rows) {
      const isNew = m.id < 0;
      const st = stratOf(m.stratId);
      const tag = isNew ? '새 행' : `${m.id}`;
      if (isNew && !st) { fail.push(`${tag}: 전략을 고르세요`); continue; }
      const name = (m.name?.trim() || (isNew && st ? `${st.code}-${st.name}`.slice(0, 64) : '')).trim();
      const amt = Number(String(m.allocAmt).replace(/,/g, ''));
      const prm = paramStr(m.params).trim();
      if (!name) { fail.push(`${tag}: 이름은 필수`); continue; }
      if (!Number.isFinite(amt) || amt < 0) { fail.push(`${tag}: 배정자금은 0 이상`); continue; }
      if (prm) {
        try { const o = JSON.parse(prm); if (typeof o !== 'object' || Array.isArray(o) || o === null) throw new Error(); }
        catch { fail.push(`${tag}: 파라미터는 JSON 객체({...})`); continue; }
      }
      try {
        await getMainAxios().post('/api/v1/pfstrat/slots', {
          id: isNew ? null : m.id, pfId, stratId: m.stratId, name, params: prm || null, allocAmt: amt,
          enabled: m.enabled === true || (m.enabled as any) === 'true',
          startD: m.startD || null, endD: m.endD || null, memo: (m.memo ?? '').trim() || null,
        });
        ok.push(m.id);
      } catch (err: any) { fail.push(`${tag} ${name}: ${err.response?.data?.error || '저장 실패'}`); }
    }
    setSaving(false);
    ed.drop(ok);
    await fetchSlots();
    if (fail.length) alert(`저장 ${ok.length}건 · 실패 ${fail.length}건 (그대로 남음)\n\n${fail.join('\n')}`);
  };

  /** 체크한 행 삭제 — 새 행은 그냥 지우고, 저장된 슬롯은 서버에서 지운다(주문이 있으면 거부 → '사용' 해제) */
  const deleteChecked = async () => {
    const sel = gridRef.current?.api.getSelectedRows() ?? [];
    if (!sel.length) { alert('지울 행을 체크하세요.'); return; }
    const fresh = sel.filter(r => r.id < 0);
    const saved = sel.filter(r => r.id > 0);
    const blocked = saved.filter(r => r.isSystem || r.nOrder > 0);
    const target = saved.filter(r => !r.isSystem && r.nOrder === 0);
    if (target.length && !confirm(`슬롯 ${target.map(r => r.name).join(', ')} 을(를) 삭제합니다.`)) return;
    ed.drop(fresh.map(r => r.id));
    const fail: string[] = [];
    for (const r of target) {
      try { await getMainAxios().delete(`/api/v1/pfstrat/slots/${r.id}`); ed.drop([r.id]); }
      catch (err: any) { fail.push(`${r.name}: ${err.response?.data?.error || '삭제 실패'}`); }
    }
    if (target.length) await fetchSlots();
    const msg = [
      ...blocked.map(r => `${r.name}: ${r.isSystem ? '시스템 슬롯은 지울 수 없음' : '주문이 있어 삭제 불가 — 사용 해제하세요'}`),
      ...fail,
    ];
    if (msg.length) alert(msg.join('\n'));
  };

  const columnDefs = useMemo<ColDef<Slot>[]>(() => [
    { field: 'id', headerName: 'ID', width: 60, cellStyle: { textAlign: 'right' }, valueFormatter: p => p.value < 0 ? '새 행' : String(p.value) },
    {
      field: 'stratId', headerName: '전략 ✎', width: 160, editable: canEdit('stratId'),
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: userStrats.map(s => s.id) },
      valueFormatter: p => { const s = stratOf(p.value); return s ? `${s.code} ${s.name}` : (p.data && p.data.id < 0 ? '(선택)' : `${p.data?.code ?? ''} ${p.data?.stratName ?? ''}`); },
    },
    { field: 'name', headerName: '슬롯 이름 ✎', width: 150, editable: canEdit('name'), cellStyle: { fontWeight: 600 },
      valueFormatter: p => p.value || (p.data && p.data.id < 0 ? '(비우면 전략 이름)' : '') },
    {
      field: 'type', headerName: '타입', width: 90, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => {
        if (!p.value) return null;
        const [t, bg, fg] = TYPE_BADGE[p.value] ?? [p.value, '#f3f4f6', '#4b5563'];
        return <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, backgroundColor: bg, color: fg }}>{t}</span>;
      },
    },
    {
      field: 'allocAmt', headerName: '배정자금 ✎', width: 120, cellStyle: { textAlign: 'right' },
      editable: canEdit('allocAmt'), cellEditor: 'agNumberCellEditor', cellEditorParams: { min: 0 },
      valueFormatter: p => won(p.value ?? 0),
    },
    {
      field: 'enabled', headerName: '사용 ✎', width: 75, cellStyle: { textAlign: 'center' },
      editable: canEdit('enabled'), cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] },
      valueFormatter: p => p.value ? '사용' : '해제',
      cellRenderer: (p: any) => p.value
        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>● 사용</span> : <span style={{ color: colors.hint }}>○ 해제</span>,
    },
    {
      field: 'params', headerName: '파라미터 ✎', flex: 1, minWidth: 220, editable: canEdit('params'),
      cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true, cellEditorParams: { maxLength: 2000, rows: 6, cols: 60 },
      cellStyle: { fontFamily: 'monospace', fontSize: '11px', color: '#374151' },
      valueGetter: p => paramStr(p.data?.params),
      cellRenderer: (p: any) => p.value
        ? p.value
        : <span style={{ color: colors.hint }}>기본값 {paramStr(p.data?.defaultParams) || '-'}</span>,
    },
    { field: 'startD', headerName: '시작일 ✎', width: 105, editable: canEdit('startD'), cellEditor: 'agDateStringCellEditor', cellStyle: { textAlign: 'center' }, valueFormatter: p => p.value ?? '' },
    { field: 'endD', headerName: '종료일 ✎', width: 105, editable: canEdit('endD'), cellEditor: 'agDateStringCellEditor', cellStyle: { textAlign: 'center' }, valueFormatter: p => p.value ?? '' },
    { field: 'memo', headerName: '메모 ✎', width: 160, editable: canEdit('memo'), valueFormatter: p => p.value ?? '' },
    ...(isDev ? [{
      headerName: '연결 사용자', width: 190, headerTooltip: '이 슬롯에 주문·조회할 수 있는 사용자 (내부·외부). 클릭: 연결 관리',
      valueGetter: (p: any) => p.data ? slotGrants(p.data.id).map(g => `${g.userName ?? g.email}${g.canOrder ? '' : '(조회)'}`).join(', ') : '',
      cellRenderer: (p: any) => p.data?.isSystem || p.data?.id < 0 ? <span style={{ color: colors.hint }}>-</span> : (
        <span onClick={() => setLinkSlot(p.data)} style={{ cursor: 'pointer', color: p.value ? colors.title : colors.hint }}>
          {p.value || '+ 연결'}
        </span>
      ),
    } as ColDef<Slot>] : []),
    { field: 'nOrder', headerName: '주문', width: 65, cellStyle: { textAlign: 'right' } },
    {
      field: 'pnl', headerName: '손익금액', width: 110, headerTooltip: '순손익 = 실현 + 평가 − 수수료 − 세금 (첫 주문일 ~ 오늘 누적)',
      cellStyle: (p: any) => ({ textAlign: 'right', fontWeight: 600, color: signColor(p.value) }),
      valueFormatter: p => (p.value == null ? '-' : won(p.value)),
    },
    {
      field: 'retPct', headerName: '수익률', width: 80, headerTooltip: '손익금액 ÷ 배정자금',
      cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }),
      valueFormatter: p => (p.value == null ? '-' : pct(p.value)),
    },
    {
      field: 'realized', headerName: '실현', width: 95,
      cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }),
      valueFormatter: p => (p.value == null ? '-' : won(p.value)),
    },
    {
      field: 'unrealized', headerName: '평가', width: 90,
      cellStyle: (p: any) => ({ textAlign: 'right', color: signColor(p.value) }),
      valueFormatter: p => (p.value == null ? '-' : won(p.value)),
    },
    {
      field: 'fee', headerName: '수수료(추정)', width: 95, headerTooltip: '매수·매도 각 0.015%',
      cellStyle: { textAlign: 'right', color: colors.down },
      valueFormatter: p => (p.value == null ? '-' : won(-p.value || 0)),
    },
    {
      field: 'tax', headerName: '세금(추정)', width: 90, headerTooltip: '증권거래세 매도대금 0.20% (ETF·ETN 면제)',
      cellStyle: { textAlign: 'right', color: colors.down },
      valueFormatter: p => (p.value == null ? '-' : won(-p.value || 0)),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canOrder, isDev, slotGrants, userStrats, stratOf]);

  const totPnl = shown.reduce((a, s) => a + (s.pnl ?? 0), 0);
  const totTax = shown.reduce((a, s) => a + (s.tax ?? 0), 0);
  const totFee = shown.reduce((a, s) => a + (s.fee ?? 0), 0);
  const over = initAmt > 0 && allocSum > initAmt;

  return (
    <div style={cardGrid}>
      <div style={{ ...titleRow, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ ...gridTitle, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          전략 슬롯 — 계좌 {pfId ?? '-'} ({shown.length}개)
          <span style={{ ...gridHint, color: over ? colors.up : colors.muted }}>
            배정 {won(allocSum)} / 초기금액 {won(initAmt)}{initAmt > 0 && ` · 미배정 ${won(initAmt - allocSum)}`}
          </span>
          {perfFrom && (
            <span style={{ ...gridHint, color: colors.muted }}>
              손익 {perfFrom} ~ 오늘 <b style={{ color: signColor(totPnl) }}>{won(totPnl)}</b> (세금 {won(-totTax || 0)} · 수수료 {won(-totFee || 0)})
            </span>
          )}
          <label style={{ fontSize: '12px', fontWeight: 400, color: colors.body, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={showSystem} onChange={e => setShowSystem(e.target.checked)} /> 시스템 슬롯
          </label>
        </h3>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {ed.count > 0 && <span style={{ fontSize: '12px', color: '#b45309', fontWeight: 600 }}>저장 안 한 변경 {ed.count}건</span>}
          {canOrder && <>
            <button onClick={addRow} disabled={!pfId} style={btn('#22c55e')}>행 추가</button>
            <button onClick={saveAll} disabled={!ed.count || saving} style={btn(ed.count && !saving ? '#16a34a' : colors.btnDisabled)}>{saving ? '저장 중…' : '변경 저장'}</button>
            <button onClick={ed.reset} disabled={!ed.count} style={btn(ed.count ? '#6b7280' : colors.btnDisabled)}>되돌리기</button>
            <button onClick={deleteChecked} style={btn(colors.up)}>삭제</button>
          </>}
          <button onClick={fetchSlots} style={btnPrimary}>새로고침</button>
        </span>
      </div>

      {error && <div style={{ marginBottom: 10, padding: '8px 12px', backgroundColor: '#fee2e2', border: '1px solid #ef4444', borderRadius: 4, color: '#991b1b', fontSize: '12px' }}>{error}</div>}

      {!pfId
        ? <p style={{ fontSize: '12px', color: colors.muted, margin: 0 }}>상단에서 계좌를 선택하세요.</p>
        : (
          <div className="ag-theme-quartz" style={agGridBox(Math.max(140, Math.min(40 + Math.max(viewShown.length, 1) * 26, 360)))}>
            <AgGridReact<Slot> {...copyMenu.gridProps}
              ref={gridRef}
              rowData={viewShown}
              columnDefs={columnDefs}
              // cellDataType: false — 값으로 형식을 추측하지 않는다. 메모가 '2026-10-23 …' 으로 시작하면 날짜 칸으로 보고
              // 날짜 편집기를 띄워 뒤 글자를 지웠다 (2026-11-03 실측). 날짜·숫자 칸은 cellEditor 를 직접 지정한다
              defaultColDef={{ resizable: true, sortable: true, filter: false, cellDataType: false, cellStyle: { fontSize: '12px' }, cellClassRules: ed.cellClassRules }}
              onCellValueChanged={ed.onCellValueChanged}
              stopEditingWhenCellsLoseFocus={true}
              rowSelection={canOrder ? { mode: 'multiRow', checkboxes: true, headerCheckbox: false, enableClickSelection: false } : undefined}
              selectionColumnDef={{ pinned: 'left', width: 36, resizable: false }}
              getRowId={p => String(p.data.id)}
              getRowStyle={p => ed.newRowStyle(p) ?? (p.data && !p.data.enabled ? { color: colors.hint } : undefined)}
              loading={loading}
            />
          </div>
        )}
      <div style={{ fontSize: '11px', color: colors.muted, marginTop: 6 }}>
        ✎ 칸은 더블클릭(또는 Enter)으로 수정 · 노란 칸 = 저장 안 한 변경 · 초록 행 = 새 행 · 삭제는 체크한 행 ·
        fdsintraday 는 재시작 없이 반영 · params 에 BuyWeiPct·MaxPosWeiPct·StopLossPct·TakeProfitPct 를 넣으면 전역 설정보다 우선
        {isDev && acctGrants.length > 0 && (
          <> · 계좌 전체 권한: {acctGrants.map(g => `${g.userName ?? g.email}${g.canOrder ? '' : '(조회)'}`).join(', ')}</>
        )}
      </div>

      {linkSlot && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setLinkSlot(null)}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', width: '520px', maxWidth: 'calc(100vw - 32px)', maxHeight: '90vh', overflow: 'auto', border: `2px solid ${colors.cardBorder}` }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', color: colors.title, fontSize: '14px', fontWeight: 600 }}>
              슬롯 사용자 — 계좌 {pfId} · 슬롯 {linkSlot.id} {linkSlot.name}
            </h3>
            <div style={{ fontSize: '11px', color: colors.muted, marginBottom: '12px' }}>
              연결된 사용자는 이 슬롯으로 주문 접수·정정·취소하고 계좌를 조회한다. 외부 사용자(API주문)는 API 토큰으로, 내부 사용자는 화면에서.
              토큰 발급은 Admin &gt; 계정관리.
            </div>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginBottom: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: colors.agHeader, color: colors.title }}>
                  <th style={{ textAlign: 'left', padding: '4px' }}>사용자</th><th>구분</th><th>권한</th><th style={{ textAlign: 'left' }}>메모</th><th></th>
                </tr>
              </thead>
              <tbody>
                {slotGrants(linkSlot.id).length === 0 && (
                  <tr><td colSpan={5} style={{ padding: '8px', color: colors.hint, textAlign: 'center' }}>연결된 사용자가 없습니다</td></tr>
                )}
                {slotGrants(linkSlot.id).map(g => (
                  <tr key={g.id} style={{ borderTop: `1px solid ${colors.divider}` }}>
                    <td style={{ padding: '4px' }}>{g.userName} <span style={{ color: colors.hint }}>{g.email}</span></td>
                    <td style={{ textAlign: 'center' }}>{g.userRole === 15 ? '외부' : '내부'}</td>
                    <td style={{ textAlign: 'center' }}>{g.canOrder ? '주문' : '조회'}</td>
                    <td>{g.memo ?? ''}</td>
                    <td style={{ textAlign: 'right' }}><button onClick={() => removeLink(g)} style={btnSm('#ef4444')}>해제</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px' }}>
              <select value={linkForm.userId} onChange={e => setLinkForm({ ...linkForm, userId: e.target.value ? Number(e.target.value) : '' })}
                style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
                <option value="">사용자 선택</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email}) · {roleNm(u.role)}</option>)}
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input type="checkbox" checked={linkForm.canOrder} onChange={e => setLinkForm({ ...linkForm, canOrder: e.target.checked })} />
                주문 허용
              </label>
              <input value={linkForm.memo} onChange={e => setLinkForm({ ...linkForm, memo: e.target.value })} placeholder="메모"
                style={{ ...inputStyle, width: 120 }} />
              <button onClick={addLink} style={btn('#16a34a')}>+ 연결</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setLinkSlot(null)} style={btn('#9ca3af')}>닫기</button>
            </div>
          </div>
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default StrategySlotManager;
